import type { MutationOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addressesLoadedAtom, bffUserProfileLoadedAtom, isLoggedInAtom } from 'app/atoms';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import type { AxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import { http } from '../lib/axios';
import { getBffQueryData } from 'app/lib/query';
import { fixPostalCode } from './address-utils';
import { addressKeys } from 'app/network/keys';
import type { Address } from 'app/types/address';

export function useAllAddresses(opts: FunctionalUseQueryOptions<Address[], AxiosError> = {}) {
  const client = useQueryClient();
  const [addressesLoaded, setAddressesLoaded] = useAtom(addressesLoadedAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);
  const queryKey = addressKeys.list('self');

  const query = useQuery<Address[], AxiosError>({
    queryKey,
    queryFn: async () => {
      const resData =
        (bffUserProfileLoaded ? getBffQueryData<Address[]>(client, queryKey) : undefined) ??
        (await http.get<Address[]>('/addresses')).data;

      setAddressesLoaded(true);
      resData.forEach((address) => client.setQueryData(addressKeys.detail(address.id), address));

      return resData;
    },
    ...opts,
    enabled: isLoggedIn && !addressesLoaded && bffUserProfileLoaded && (opts.enabled ?? true),
  });

  return query;
}

export function useOneAddress(id?: Address['id'], opts: FunctionalUseQueryOptions<Address | null> = {}) {
  return useQuery<Address | null>({
    queryKey: addressKeys.detail(id ?? 'meh'),
    queryFn: () => (!id ? null : http.get<Address>(`/addresses/${id}`).then((res) => res.data)),
    ...opts,
  });
}

export type GeoCodeAddressResponse = Partial<Address> & {
  latitude: number;
  longitude: number;
  postalCodeTooLarge: boolean;
};

export type ReverseGeocodeAddressResponse = Partial<
  Pick<Address, 'administrativeArea' | 'city' | 'country' | 'postalCode' | 'street'>
>;

interface GeocodeAddressSearchInput {
  address: string;
  country: string;
}

export function useGeocodeAddressSearch(
  opts: MutationOptions<GeoCodeAddressResponse, Error, GeocodeAddressSearchInput> = {},
) {
  return useMutation<GeoCodeAddressResponse, Error, GeocodeAddressSearchInput>({
    mutationFn: ({ address, country }) =>
      http
        .get<GeoCodeAddressResponse>('/addresses/geocode', {
          // The API geocoder accepts either a postal code or a free-form address
          // through this legacy parameter. Do not run fixPostalCode here because
          // it intentionally removes spaces from postal codes.
          params: { postalCode: address, country },
        })
        .then((res) => res.data),
    ...opts,
  });
}

export function useReverseGeocodeAddress(
  opts: MutationOptions<ReverseGeocodeAddressResponse, Error, { latitude: number; longitude: number }> = {},
) {
  return useMutation<ReverseGeocodeAddressResponse, Error, { latitude: number; longitude: number }>({
    mutationFn: ({ latitude, longitude }) =>
      http
        .get<ReverseGeocodeAddressResponse>('/addresses/reverse-geocode', {
          params: { latitude, longitude },
          timeout: 10_000,
        })
        .then((res) => res.data),
    ...opts,
  });
}

export function useReverseGeocodedAddress(latitude?: number, longitude?: number, enabled = true) {
  return useQuery<ReverseGeocodeAddressResponse>({
    queryKey: ['addresses', 'reverse-geocode', latitude, longitude],
    enabled: enabled && latitude !== undefined && longitude !== undefined,
    staleTime: Infinity,
    retry: false,
    queryFn: () =>
      http
        .get<ReverseGeocodeAddressResponse>('/addresses/reverse-geocode', {
          params: { latitude, longitude },
          timeout: 10_000,
        })
        .then((res) => res.data),
  });
}

export function useOneGeoAddress(
  postalCode?: Address['postalCode'],
  country?: Address['country'],
  opts: FunctionalUseQueryOptions<GeoCodeAddressResponse | null> = {},
) {
  const queryKey = ['addresses', 'geo', postalCode, country] as const;
  return useQuery<GeoCodeAddressResponse | null>({
    queryKey,
    queryFn: async () => {
      if (!postalCode || !country) return null;
      const res = await http.get<GeoCodeAddressResponse>('/addresses/geocode', {
        params: {
          postalCode: fixPostalCode(postalCode, country),
          country,
        },
      });
      return res.data;
    },
    retry: false,
    ...opts,
  });
}
